// ── SegmentedControl ──────────────────────────────────────────────────────────
// Pill-group style toggle — communicates "choose ONE from these options".
// Visual: shared border around all segments, active segment = cyan fill.
// Drop-in replacement for scattered Button-map patterns in the email toolbar.

import { useState } from 'react';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  size?: 'sm' | 'md';
}

const H:  Record<'sm' | 'md', string> = { sm: '28px', md: '32px' };
const PX: Record<'sm' | 'md', string> = { sm: '10px', md: '14px' };
const FS: Record<'sm' | 'md', string> = { sm: '12px', md: '13px' };

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = 'sm',
}: Props<T>) {
  const [hovered, setHovered] = useState<T | null>(null);

  return (
    <div
      style={{
        display: 'inline-flex',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {options.map((opt, i) => {
        const isActive  = opt.value === value;
        const isHovered = hovered === opt.value && !isActive;

        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            onMouseEnter={() => setHovered(opt.value)}
            onMouseLeave={() => setHovered(null)}
            style={{
              height: H[size],
              padding: `0 ${PX[size]}`,
              fontSize: FS[size],
              fontWeight: 500,
              fontFamily: 'var(--evo-font)',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              border: 'none',
              outline: 'none',
              userSelect: 'none',
              // Divider between segments
              borderRight: i < options.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
              // Active: cyan fill
              background: isActive
                ? 'var(--evo-cyan)'
                : isHovered
                ? 'var(--evo-bg-3)'
                : 'transparent',
              color: isActive
                ? 'var(--evo-fg-on-cyan)'
                : 'var(--evo-fg-2)',
              transition: 'background 120ms linear, color 120ms linear',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
